import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';

const DrillLogList = () => {
  const navigate = useNavigate();
  const [drillLogs, setDrillLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [drillLogToDelete, setDrillLogToDelete] = useState(null);

  useEffect(() => {
    fetchDrillLogs();
  }, []);

  const fetchDrillLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/drill_logs/');
      if (response.data.data) {
        setDrillLogs(response.data.data);
      }
    } catch (error) {
      console.error('시추주상도 목록 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!drillLogToDelete) return;

    try {
      await axios.delete(`/drill_logs/${drillLogToDelete.id}`);
      setDrillLogs(drillLogs.filter(log => log.id !== drillLogToDelete.id));
      setDeleteModalOpen(false);
      setDrillLogToDelete(null);
    } catch (error) {
      console.error('시추주상도 삭제 실패:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  const openDeleteModal = (drillLog, e) => {
    e.stopPropagation(); // 행 클릭 이벤트 방지
    setDrillLogToDelete(drillLog);
    setDeleteModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-secondary-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-secondary-50">
      <div className="max-w-7xl mx-auto p-8">
        {/* 헤더 */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">시추 주상도 목록</h1>
          <p className="text-secondary-600">업로드된 시추 주상도를 확인하고 관리합니다</p>
        </div>

        {/* 테이블 */}
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {drillLogs.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-20 h-20 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-secondary-600 mb-4">등록된 시추 주상도가 없습니다</p>
              <p className="text-sm text-secondary-500">신규 데이터 업로드를 통해 시추 주상도를 등록해보세요</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-secondary-50 border-b border-secondary-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                    이름
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                    시추공 개수
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                    생성일시
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-secondary-700 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-200">
                {drillLogs.map((drillLog) => (
                  <tr
                    key={drillLog.id}
                    onClick={() => navigate(`/drill-logs/${drillLog.id}`)}
                    className="hover:bg-secondary-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                      #{drillLog.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-secondary-900">{drillLog.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                      {drillLog.borehole_count}개
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                      {new Date(drillLog.created_at).toLocaleDateString('ko-KR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={(e) => openDeleteModal(drillLog, e)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 삭제 확인 모달 */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-hard animate-fade-in">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-secondary-900">시추 주상도 삭제</h3>
            </div>
            <p className="text-secondary-600 mb-6">
              <span className="font-semibold">{drillLogToDelete?.name}</span>을(를) 삭제하시겠습니까?<br />
              관련된 모든 시추공 및 SPT 데이터도 함께 삭제됩니다.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setDrillLogToDelete(null);
                }}
                className="flex-1 px-4 py-2 bg-secondary-100 text-secondary-700 rounded-xl hover:bg-secondary-200 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrillLogList;
